import React from 'react';

import type { SxProps, Theme } from '@mui/material/styles';
import Decimal from 'decimal.js-light';
import { NumericFormat as NumberFormat } from 'react-number-format';

import { formatToPercentage } from 'utils/format';

import PercentageText from 'components/shared/PercentageText';
import { formats } from 'components/shared/ConfigurationTableModal/constants';
import CustomBodyTableCell from 'components/shared/CustomTable/CustomBodyTableCell';

import type { Column } from 'api/shared/models';

interface Props {
  row: any;
  column: Column;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  handleContextMenu?: (
    event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
    column: Column,
    row: any,
  ) => void;
}

const CustomTableCellValue = ({
  row,
  column,
  style = {},
  sx,
  onClick = () => {},
  handleContextMenu,
}: Props) => {
  const addComma = (num: number | undefined | null, isInteger = false) => {
    if (num === undefined || num === null) return '';
    const putDecimals = !isInteger
      ? { minimumFractionDigits: 2, maximumSignificantDigits: 2 }
      : {};
    return new Intl.NumberFormat('en-US', putDecimals).format(num);
  };

  if (column.collapsed) {
    return (
      <CustomBodyTableCell
        onClick={onClick}
        rowSpan={column.rowSpan}
        sx={sx}
        onContextMenu={(event) =>
          handleContextMenu && handleContextMenu(event, column, row)
        }
      />
    );
  }

  switch (column?.format) {
    case formats.TEXT:
      return (
        <CustomBodyTableCell
          sx={sx}
          style={style}
          align={column.align || 'left'}
          onClick={onClick}
          rowSpan={column.rowSpan}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        >
          {column.fixedColumn || column.tooltip ? (
            <span title={row[column.tooltip || ''] || row[column.apiField]}>
              {row[column.apiField]}
            </span>
          ) : (
            <>{row[column.apiField]}</>
          )}
        </CustomBodyTableCell>
      );
    case formats.INT:
      return (
        <CustomBodyTableCell
          sx={sx}
          style={style}
          align={column.align || 'right'}
          onClick={onClick}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        >
          {column.prefix ? column.prefix : null}
          {addComma(row[column.apiField], true)}
        </CustomBodyTableCell>
      );
    case formats.FLOAT:
      return (
        <CustomBodyTableCell
          sx={sx}
          style={style}
          align={column.align || 'right'}
          onClick={onClick}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        >
          <NumberFormat
            thousandSeparator
            displayType={'text'}
            prefix={column.prefix}
            suffix={column.suffix}
            decimalScale={column.decimalScale || 2}
            fixedDecimalScale
            value={`${row[column.apiField]}`}
          />
        </CustomBodyTableCell>
      );
    case formats.PERCENTAGE:
      return (
        <CustomBodyTableCell
          sx={sx}
          align={column.align || 'right'}
          style={style}
          onClick={onClick}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        >
          <PercentageText
            value={
              row[column.apiField]
                ? formatToPercentage(row[column.apiField], 2)
                : '0'
            }
          />
        </CustomBodyTableCell>
      );
    case formats.PERCENTAGE_SCALE:
      return (
        <CustomBodyTableCell
          sx={sx}
          align={column.align || 'right'}
          style={style}
          onClick={onClick}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        >
          <PercentageText
            value={
              row[column.apiField]
                ? new Decimal(row[column.apiField]).times(100).toNumber()
                : '0'
            }
            decimalScale={1}
          />
        </CustomBodyTableCell>
      );
    default:
      return (
        <CustomBodyTableCell
          onClick={onClick}
          rowSpan={column.rowSpan}
          sx={sx}
          onContextMenu={(event) =>
            handleContextMenu && handleContextMenu(event, column, row)
          }
        />
      );
  }
};

export default CustomTableCellValue;
